/**
 * Tiny Web Audio synth. No sample files. Unlocks on the first Play tap.
 */
export class AudioBus {
  private ctx: AudioContext | null = null;
  muted = false;

  unlock(): void {
    if (!this.ctx) {
      const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.ctx = new Ctx();
    }
    if (this.ctx.state === "suspended") void this.ctx.resume();
  }

  toggleMute(): boolean {
    this.muted = !this.muted;
    return this.muted;
  }

  jump(): void {
    this.blip(320, 520, 0.09, "triangle", 0.07);
  }

  land(): void {
    this.noise(0.05, 0.04, 180);
  }

  lightUp(index: number): void {
    const root = 392;
    const steps = [0, 4, 7, 12, 16];
    const f = root * Math.pow(2, (steps[index % steps.length] ?? 0) / 12);
    this.blip(f, f * 1.01, 0.22, "sine", 0.09);
    this.blip(f * 2, f * 2, 0.12, "triangle", 0.03);
  }

  clip(): void {
    this.blip(180, 90, 0.14, "sawtooth", 0.05);
    this.noise(0.08, 0.05, 90);
  }

  fall(): void {
    this.blip(240, 70, 0.55, "triangle", 0.08);
    this.noise(0.3, 0.06, 60);
  }

  private blip(
    f0: number,
    f1: number,
    dur: number,
    type: OscillatorType,
    gain: number,
  ): void {
    if (this.muted || !this.ctx) return;
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(f0, t);
    osc.frequency.exponentialRampToValueAtTime(Math.max(40, f1), t + dur);
    g.gain.setValueAtTime(gain, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    osc.connect(g).connect(this.ctx.destination);
    osc.start(t);
    osc.stop(t + dur + 0.02);
  }

  private noise(dur: number, gain: number, freq: number): void {
    if (this.muted || !this.ctx) return;
    const t = this.ctx.currentTime;
    const buffer = this.ctx.createBuffer(1, this.ctx.sampleRate * dur, this.ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    const src = this.ctx.createBufferSource();
    src.buffer = buffer;
    const filter = this.ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = freq;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(gain, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(filter).connect(g).connect(this.ctx.destination);
    src.start(t);
    src.stop(t + dur);
  }
}
